import { memo, useCallback } from 'react'
import { format } from 'date-fns'

import { DDELicensePeriodResponse } from '@img-arena/consoles-api-dde'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import { Checkbox, FormControlLabel, Grid, IconButton, Tooltip } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { SelectBox } from '@/components/inputs/SelectBox'
import { ConsoleTextField } from '@/components/inputs/StyledInputComponents'
import { SimpleCollapsible } from '@/components/SimpleCollapsible'
import { DATE_FORMAT } from '@/constants/dateFormat'

export type LicensePeriodType = {
  id?: number
  startDate: string
  endDate: string
  year: string
  numOfBookedEvents?: number
  numOfConsumedEvents?: number
  numOfEvents?: number
  unlimited?: boolean
  suspended?: boolean
  subscription?: DDELicensePeriodResponse['subscription']
}

type Props = {
  value?: LicensePeriodType
  onDelete: () => void
  onChange: (period: Partial<LicensePeriodType>) => void
  expanded?: boolean
  usedYears: Set<string>
  onExpandedChange?: (expanded: boolean) => void
  hasError?: string | boolean
}

const MIN_LICENSE_YEAR = 2010

const MAX_LICENSE_YEAR = 2050

export const LICENSING_PERIOD_YEARS = Array.from(
  { length: MAX_LICENSE_YEAR - MIN_LICENSE_YEAR + 1 },
  (_, i) => MIN_LICENSE_YEAR + i
).map((year) => year.toString())

const LicensingPeriodComponent = ({
  value,
  onDelete,
  onChange,
  expanded,
  usedYears = new Set(),
  onExpandedChange,
  hasError = false
}: Props) => {
  const handleYearChange = useCallback(
    (year: string) => {
      onChange({
        year,
        startDate: `${year}-01-01`,
        endDate: `${year}-12-31`
      })
    },
    [onChange]
  )

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      if (!date) {
        onChange({ startDate: '' })
        return
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      onChange({
        startDate: `${year}-${month}-${day}`
      })
    },
    [onChange]
  )

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      if (!date) {
        onChange({ endDate: '' })
        return
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      onChange({
        endDate: `${year}-${month}-${day}`
      })
    },
    [onChange]
  )

  const handleBookingsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({
        numOfBookedEvents: Number(e.target.value) < 0 ? 0 : Number(e.target.value)
      })
    },
    [onChange]
  )

  const handleUnlimitedChange = useCallback(
    (checked: boolean) => {
      onChange({
        unlimited: checked,
        numOfBookedEvents: checked ? 0 : (value?.numOfBookedEvents ?? 0)
      })
    },
    [onChange, value?.numOfBookedEvents]
  )

  const description = [
    value?.startDate &&
      value?.endDate &&
      `${format(new Date(value.startDate), 'MMM dd')} - ${format(new Date(value.endDate), 'MMM dd')}`,
    value?.unlimited
      ? 'Unlimited bookings'
      : value?.numOfBookedEvents && `${value.numOfBookedEvents} bookings`
  ]
    .filter(Boolean)
    .join(' ⋅ ')

  return (
    <SimpleCollapsible
      title={value?.year ? `${value.year}` : 'New Period'}
      expanded={expanded}
      onChange={onExpandedChange}
      description={description}
      error={hasError}
      actions={
        <Tooltip
          title={!!value?.id && 'Cannot remove previously saved license period'}
          arrow
          placement="top"
        >
          <span>
            <IconButton onClick={onDelete} size="small" disabled={!!value?.id}>
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      <Grid container rowSpacing={1} columnSpacing={2} pb={0.25}>
        <Grid size={{ xs: 3 }}>
          <SelectBox
            options={LICENSING_PERIOD_YEARS.filter((year) => !usedYears.has(year))}
            value={value?.year || ''}
            onChange={handleYearChange}
            label="Year"
            fullWidth
          />
        </Grid>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid size={{ xs: 4.5 }}>
            <DatePicker
              label="Start Date"
              value={value?.startDate ? new Date(value.startDate) : null}
              format={DATE_FORMAT.DATE}
              onChange={handleStartDateChange}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true
                }
              }}
              minDate={value?.year ? new Date(`${value.year}-01-01`) : undefined}
              maxDate={value?.year ? new Date(`${value.year}-12-31`) : undefined}
              views={['day']}
              openTo="day"
              shouldDisableYear={(date) => date.getFullYear().toString() !== value?.year}
              disabled={!value?.year}
            />
          </Grid>
          <Grid size={{ xs: 4.5 }}>
            <DatePicker
              label="End Date"
              value={value?.endDate ? new Date(value.endDate) : null}
              format={DATE_FORMAT.DATE}
              onChange={handleEndDateChange}
              minDate={value?.startDate ? new Date(value.startDate) : undefined}
              maxDate={value?.year ? new Date(`${value.year}-12-31`) : undefined}
              views={['day']}
              openTo="day"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true
                }
              }}
              shouldDisableYear={(date) => date.getFullYear().toString() !== value?.year}
              disabled={!value?.year}
            />
          </Grid>
        </LocalizationProvider>

        <Grid size={{ xs: 3 }}>
          <ConsoleTextField
            label="Bookings"
            type="number"
            value={value?.numOfBookedEvents ?? 0}
            disabled={value?.unlimited || !value?.year}
            onChange={handleBookingsChange}
          />
        </Grid>
        <Grid size={{ xs: 9 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={value?.unlimited || false}
                onChange={({ target: { checked } }) => handleUnlimitedChange(checked)}
              />
            }
            label="Unlimited Bookings"
            disabled={!value?.year}
            sx={{
              whiteSpace: 'nowrap',
              mt: 1,
              ml: 0
            }}
          />
        </Grid>
      </Grid>
    </SimpleCollapsible>
  )
}

export const LicensingPeriod = memo(LicensingPeriodComponent)
